// Canva API integration for image editing
// This utility handles creating designs in Canva and syncing back edited images

const CANVA_API_BASE = 'https://api.canva.com/rest/v1';

interface CanvaDesignResponse {
  design: {
    id: string;
    title: string;
    thumbnail: {
      url: string;
    };
    urls: {
      edit_url: string;
      view_url: string;
    };
  };
}

interface CanvaExportResponse {
  job: {
    id: string;
    status: 'in_progress' | 'success' | 'error';
    url?: string;
  };
}

class CanvaApiError extends Error {
  constructor(message: string, public status?: number) {
    super(message);
    this.name = 'CanvaApiError';
  }
}

class CanvaApi {
  private accessToken: string;

  constructor(accessToken: string) {
    this.accessToken = accessToken;
  }

  private async makeRequest(endpoint: string, options: RequestInit = {}) {
    const url = `${CANVA_API_BASE}${endpoint}`;
    
    const response = await fetch(url, {
      ...options,
      headers: {
        'Authorization': `Bearer ${this.accessToken}`,
        'Content-Type': 'application/json',
        ...options.headers,
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => null);
      throw new CanvaApiError(
        errorData?.message || `HTTP ${response.status}: ${response.statusText}`,
        response.status
      );
    }

    return response.json();
  }

  /**
   * Create a new Canva design with the specified image as the base asset
   */
  async createDesignWithImage(
    imageUrl: string,
    title: string,
    options: {
      width?: number;
      height?: number;
      designType?: string;
    } = {}
  ): Promise<CanvaDesignResponse> {
    const payload = {
      design_type: options.designType || 'presentation',
      title,
      assets: [
        {
          type: 'image',
          upload_url: imageUrl,
          position: {
            x: 0,
            y: 0,
          },
          dimensions: {
            width: options.width || 800,
            height: options.height || 600,
          },
        },
      ],
    };

    console.log('🎨 [Canva API] Creating design with payload:', payload);

    return this.makeRequest('/designs', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  /**
   * Export a Canva design as an image
   */
  async exportDesign(
    designId: string,
    format: 'png' | 'jpg' | 'pdf' = 'png'
  ): Promise<CanvaExportResponse> {
    const payload = {
      format,
      quality: 'high',
    };

    console.log('📤 [Canva API] Exporting design:', designId);

    return this.makeRequest(`/designs/${designId}/export`, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  /**
   * Check the status of an export job
   */
  async getExportStatus(jobId: string): Promise<CanvaExportResponse> {
    return this.makeRequest(`/export/jobs/${jobId}`);
  }

  /**
   * Get design details
   */
  async getDesign(designId: string) {
    return this.makeRequest(`/designs/${designId}`);
  }

  /**
   * Generate the edit URL for a design
   */
  generateEditUrl(designId: string, returnUrl?: string): string {
    const baseUrl = `https://www.canva.com/design/${designId}/edit`;
    if (returnUrl) {
      return `${baseUrl}?return_url=${encodeURIComponent(returnUrl)}`;
    }
    return baseUrl;
  }

  /**
   * Poll export job until completion
   */
  async waitForExport(jobId: string, maxAttempts: number = 30): Promise<string> {
    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      const result = await this.getExportStatus(jobId);
      
      if (result.job.status === 'success' && result.job.url) {
        return result.job.url;
      }
      
      if (result.job.status === 'error') {
        throw new CanvaApiError('Export job failed');
      }

      // Wait 2 seconds before next attempt
      await new Promise(resolve => setTimeout(resolve, 2000));
    }

    throw new CanvaApiError('Export job timed out');
  }
}

// Main functions for the application
export interface CanvaEditSession {
  designId: string;
  editUrl: string;
  assetId: string;
  imageType: 'primary' | 'secondary';
  originalImageUrl: string;
}

/**
 * Start a Canva editing session for an image
 */
export async function startCanvaEditSession(
  imageUrl: string,
  assetId: string,
  imageType: 'primary' | 'secondary',
  title: string
): Promise<CanvaEditSession> {
  const { getStoredCanvaToken } = await import('@/utils/canvaAuth');
  
  // Check if we have a valid access token
  const accessToken = getStoredCanvaToken();
  
  if (!accessToken) {
    // No token available - this should not happen as we check auth before calling
    throw new CanvaApiError('No valid Canva access token available');
  }

  const canva = new CanvaApi(accessToken);
  

  // Create design with the image
  const designResponse = await canva.createDesignWithImage(imageUrl, title);
  
  // Generate return URL to come back to our app
  const currentUrl = window.location.origin;
  const returnUrl = `${currentUrl}/canva-callback?designId=${designResponse.design.id}&assetId=${assetId}&imageType=${imageType}`;
  
  // Generate edit URL with return callback
  const editUrl = canva.generateEditUrl(designResponse.design.id, returnUrl);

  const session: CanvaEditSession = {
    designId: designResponse.design.id,
    editUrl,
    assetId,
    imageType,
    originalImageUrl: imageUrl,
  };

  // Store session data for later retrieval
  localStorage.setItem(`canva_session_${assetId}_${imageType}`, JSON.stringify(session));

  console.log('✅ [Canva Integration] Edit session created:', {
    designId: session.designId,
    editUrl: session.editUrl,
  });

  return session;

  // TODO: Uncomment when API credentials are available
  // const canva = new CanvaApi(accessToken);
  // 
  // console.log('🎨 [Canva Integration] Starting edit session for:', {
  //   assetId,
  //   imageType,
  //   imageUrl: imageUrl.substring(0, 50) + '...',
  // });
  //
  // // Create design with the image
  // const designResponse = await canva.createDesignWithImage(imageUrl, title);
  // 
  // // Generate return URL to come back to our app
  // const currentUrl = window.location.origin;
  // const returnUrl = `${currentUrl}/canva-callback?designId=${designResponse.design.id}&assetId=${assetId}&imageType=${imageType}`;
  // 
  // // Generate edit URL with return callback
  // const editUrl = canva.generateEditUrl(designResponse.design.id, returnUrl);
  //
  // const session: CanvaEditSession = {
  //   designId: designResponse.design.id,
  //   editUrl,
  //   assetId,
  //   imageType,
  //   originalImageUrl: imageUrl,
  // };
  //
  // // Store session data for later retrieval
  // localStorage.setItem(`canva_session_${assetId}_${imageType}`, JSON.stringify(session));
  //
  // console.log('✅ [Canva Integration] Edit session created:', {
  //   designId: session.designId,
  //   editUrl: session.editUrl,
  // });
  //
  // return session;
}

/**
 * Sync back the edited image from Canva
 */
export async function syncCanvaEditedImage(
  designId: string,
  assetId: string,
  imageType: 'primary' | 'secondary'
): Promise<string> {
  const { getStoredCanvaToken } = await import('@/utils/canvaAuth');
  
  const accessToken = getStoredCanvaToken();
  
  if (!accessToken) {
    throw new CanvaApiError('No valid Canva access token available');
  }

  const canva = new CanvaApi(accessToken);
  
  console.log('🔄 [Canva Integration] Syncing edited image:', {
    designId,
    assetId,
    imageType,
  });

  // Export the design
  const exportResponse = await canva.exportDesign(designId);
  
  if (!exportResponse.job.id) {
    throw new CanvaApiError('Failed to start export job');
  }

  // Wait for export to complete
  const editedImageUrl = await canva.waitForExport(exportResponse.job.id);

  console.log('✅ [Canva Integration] Edited image ready:', editedImageUrl);

  // Clean up session data
  localStorage.removeItem(`canva_session_${assetId}_${imageType}`);

  return editedImageUrl;
}

/**
 * Get stored Canva session data
 */
export function getCanvaSession(assetId: string, imageType: 'primary' | 'secondary'): CanvaEditSession | null {
  const sessionData = localStorage.getItem(`canva_session_${assetId}_${imageType}`);
  if (!sessionData) return null;
  
  try {
    return JSON.parse(sessionData);
  } catch (error) {
    console.error('Failed to parse Canva session data:', error);
    return null;
  }
}

// Direct Canva editing function for immediate use (no modal)
export const openCanvaEditorDirect = async (asset: any, imageUrl: string, toastFn?: any, options?: { reopenCropEditor?: boolean; dimensions?: { width: number; height: number; }; imageType?: 'primary' | 'secondary'; mode?: 'crop' | 'markup'; redirect?: { originPage: string; customReturnPath?: string; additionalState?: Record<string, any>; } }): Promise<void> => {
  if (!imageUrl) {
    if (toastFn) {
      toastFn({
        title: "No Image Available",
        description: "This asset doesn't have an image to edit.",
        variant: "destructive",
      });
    }
    return;
  }

  try {
    console.log('🎨 [Canva API Direct] Starting edit flow for asset:', asset.title);

    const { invokeEdgeFunction } = await import('@/utils/edgeFunctionUtils');
    const { supabase } = await import('@/integrations/supabase/client');

    // First determine which image type we're working with
    const imageType = options?.imageType || (
      imageUrl === asset.secondary_image || 
      (asset.secondary_markup_url && imageUrl === asset.secondary_markup_url) ||
      imageUrl.includes(asset.secondary_image?.split('/').pop()?.split('.')[0] || 'secondary_fallback')
      ? 'secondary' : 'primary'
    );
    
    // Check if we should look for existing markup - only in markup mode
    const relevantMarkupUrl = imageType === 'secondary' ? asset.secondary_markup_url : asset.primary_markup_url;
    const hasExistingMarkup = relevantMarkupUrl && relevantMarkupUrl.trim() !== '';
    const shouldReuseExisting = options?.mode === 'markup' && hasExistingMarkup;
    
    if (shouldReuseExisting) {
      console.log('🔍 [Canva API Direct] Asset has existing markup, checking for existing design...', {
        imageType,
        relevantMarkupUrl,
        imageUrl: imageUrl,
        hasMarkupUrl: !!relevantMarkupUrl
      });
      
      const designKey = `canva_design_${asset.id}_${imageType}`;
      const isSecondaryImage = imageType === 'secondary';
      
      console.log('🔍 [Canva Design Key] Using design key:', {
        designKey,
        imageType,
        isSecondaryImage,
        imageUrl: imageUrl.substring(0, 100) + '...'
      });
      
      const existingDesign = localStorage.getItem(designKey);
      
      if (existingDesign) {
        try {
          const parsedDesign = JSON.parse(existingDesign);
          console.log('✅ [Canva API Direct] Found existing design, opening for editing:', parsedDesign.designId);
          
          // Verify the design still exists by trying to access it
          const canvaEditUrl = parsedDesign.editUrl;
          
          const correlationState = {
            originPage: options?.redirect?.originPage || 'ASSET_EDITOR',
            assetId: asset.id,
            collectionId: asset.collection_id,
            reopenCropEditor: options?.reopenCropEditor || false,
            customReturnPath: options?.redirect?.customReturnPath,
            ...options?.redirect?.additionalState
          };

          // Store correlation state
          const actualDesignId = parsedDesign.designId;
          const correlationKey = `canva_correlation_${actualDesignId}`;
          localStorage.setItem(correlationKey, JSON.stringify(correlationState));
          
          // Create navigate URL with correlation state
          const redirectUrl = new URL(canvaEditUrl);
          const encodedCorrelationState = btoa(JSON.stringify(correlationState));
          redirectUrl.searchParams.append(
            "correlation_state", 
            encodeURIComponent(encodedCorrelationState)
          );
          const navigateUrl = redirectUrl.toString();

          if (toastFn) {
            toastFn({
              title: "Opening Existing Design",
              description: "Opening your existing Canva design...",
              duration: 1000,
            });
          }

          setTimeout(() => {
            console.log('🚀 [Canva API Direct] Redirecting to existing Canva design:', navigateUrl);
            window.open(navigateUrl, "_self");
          }, 500);
          
          return; // Exit early - we're using existing design
          
        } catch (parseError) {
          console.warn('⚠️ [Canva API Direct] Failed to parse existing design data, will create new:', parseError);
          // Fall through to create new design
        }
      } else {
        console.log('🔍 [Canva API Direct] No existing design found in localStorage, will create new');
      }
    } else {
      console.log('🔨 [Canva API Direct] No existing markup found, creating new design with current image...');
    }

    // Create new design (either no existing markup or failed to find existing design)
      
      const { data: { session: authSession }, error: sessionError } = await supabase.auth.getSession();
      if (sessionError) {
        console.error('❌ [Canva API] Session error:', sessionError);
        throw new Error('Failed to get authentication session. Please refresh the page and log in again.');
      }
      
      if (!authSession || !authSession.access_token) {
        console.error('❌ [Canva API] No valid session found');
        throw new Error('You must be logged in to use Canva integration. Please log in and try again.');
      }
      
      // Check if session is expired
      if (authSession.expires_at) {
        const expiresAt = new Date(authSession.expires_at * 1000); // expires_at is in seconds
        const now = new Date();
        if (expiresAt <= now) {
          console.error('❌ [Canva API] Session expired');
          throw new Error('Your session has expired. Please refresh the page and log in again.');
        }
      }
      
      console.log('🔐 [Canva API] Auth session status:', {
        hasSession: !!authSession,
        hasAccessToken: !!authSession?.access_token,
        tokenLength: authSession?.access_token?.length,
        expiresAt: authSession?.expires_at,
        expiresAtDate: authSession?.expires_at ? new Date(authSession.expires_at * 1000).toISOString() : null,
        user: authSession?.user?.email
      });

      // Clean up existing design logic:
      // UPDATED LOGIC:
      // - If in CROP mode: Always create fresh design (clean up any old design)  
      // - If in MARKUP mode but no existing markup: create fresh design (clean up any old design)
      // - If in MARKUP mode with existing markup: preserve existing design (handled above)
      if (options?.mode === 'crop' || !hasExistingMarkup) {
        const modeReason = options?.mode === 'crop' 
          ? 'crop mode (always creates new)'
          : 'no existing markup';
        console.log(`🔄 [Canva API] Creating fresh design due to ${modeReason}, cleaning up any old design`);
        
        // Use the imageType already determined at the top of the function
        const designKey = `canva_design_${asset.id}_${imageType}`;
        
        const existingDesign = localStorage.getItem(designKey);
        if (existingDesign) {
          try {
            const parsedDesign = JSON.parse(existingDesign);
            console.log('🗑️ [Canva API] Found old design to cleanup:', parsedDesign.designId);
            
            // Try to delete existing design, but don't fail if the function doesn't exist
            try {
              await invokeEdgeFunction(
                'canva-delete-design',
                { designId: parsedDesign.designId }
              );
              console.log('✅ [Canva API] Old design deleted successfully');
            } catch (deleteError) {
              console.warn('⚠️ [Canva API] Failed to delete old design (function may not exist, continuing anyway):', deleteError);
            }
          } catch (parseError) {
            console.warn('⚠️ [Canva API] Failed to parse old design data:', parseError);
          }
          
          // Clear the stored design info to start fresh
          localStorage.removeItem(designKey);
        }
      } else {
        console.log('✅ [Canva API] Asset has existing markup - preserving any existing design for continued editing');
      }

      // Optimize image URL for high quality upload to Canva
      const optimizedImageUrl = optimizeImageUrlForCanva(imageUrl);
      console.log('🎯 [Canva API] Image URL transformation for upload:', {
        originalReceived: imageUrl.substring(0, 100) + '...',
        optimizedForUpload: optimizedImageUrl.substring(0, 100) + '...',
        isExactCropURL: imageUrl.includes('c_crop'),
        fullOriginalURL: imageUrl,
        fullOptimizedURL: optimizedImageUrl
      });
      
      // 🚨 CRITICAL DEBUG: Compare what we're sending vs what debug showed
      console.error('🚨 [CRITICAL] URL BEING SENT TO CANVA UPLOAD:', {
        whatWeReceived: imageUrl,
        whatWeSendToCanva: optimizedImageUrl,
        areTheyIdentical: imageUrl === optimizedImageUrl,
        cropParamsInOriginal: imageUrl.match(/c_crop[^\/]*/)?.[0] || 'NONE',
        cropParamsInOptimized: optimizedImageUrl.match(/c_crop[^\/]*/)?.[0] || 'NONE',
        cropParamsChanged: imageUrl.includes('c_crop') && optimizedImageUrl.includes('c_crop') 
          ? imageUrl.match(/c_crop[^\/]*/)?.[0] !== optimizedImageUrl.match(/c_crop[^\/]*/)?.[0]
          : 'N/A'
      });
      
      // 🔥 DEBUG OUTPUT FOR REFERENCE
      console.error('🔥🔥🔥 [CANVA URL DEBUG] 🔥🔥🔥');
      console.error('URL BEING SENT TO CANVA:', optimizedImageUrl);
      console.error('🔥🔥🔥 [CANVA URL DEBUG] 🔥🔥🔥');

      // Upload asset to Canva
      const { data: uploadResult, error: uploadError } = await invokeEdgeFunction(
        'canva-upload-single-asset',
        {
          asset: {
            id: asset.id,
            name: asset.title || `Asset ${asset.id}`,
            url: optimizedImageUrl,
            tags: [asset.slug || 'pricing-explorer']
          },
          folderId: 'root',
          collectionId: asset.collection_id
        }
      );

      if (uploadError || !uploadResult?.success) {
        console.error('❌ [Canva API] Upload failed - Full error details:', {
          uploadError: uploadError,
          uploadResult: uploadResult,
          errorMessage: uploadError?.message,
          errorName: uploadError?.name,
          errorStatus: uploadError?.status,
          uploadResultError: uploadResult?.error,
          uploadResultDetails: uploadResult?.details,
          fullUploadError: JSON.stringify(uploadError, null, 2),
          fullUploadResult: JSON.stringify(uploadResult, null, 2)
        });
        
        // Handle structured error responses from the improved backend
        let errorMessage = 'Failed to upload asset to Canva';
        let isStructuredError = false;
        
        // Check if uploadError contains structured error data (from our improved edgeFunctionUtils)
        const structuredError = uploadError || uploadResult?.error;
        
        if (structuredError && typeof structuredError === 'object') {
          // Check for our structured error types
          if (structuredError.error === 'canva_token_expired') {
            errorMessage = structuredError.message || 'Canva authorization has expired. Please reconnect to Canva.';
            isStructuredError = true;
          } else if (structuredError.error === 'canva_not_connected') {
            errorMessage = structuredError.message || 'No Canva authorization found. Please connect to Canva first.';
            isStructuredError = true;
          } else if (structuredError.message) {
            errorMessage = structuredError.message;
          } else if (structuredError.error && typeof structuredError.error === 'string') {
            errorMessage = structuredError.error;
          }
          
          if (structuredError.details && !isStructuredError) {
            errorMessage += ` (Details: ${JSON.stringify(structuredError.details)})`;
          }
        } else if (typeof structuredError === 'string') {
          errorMessage = structuredError;
        } else if (uploadResult?.error && typeof uploadResult.error === 'string') {
          errorMessage = uploadResult.error;
        } else if (uploadError?.message) {
          errorMessage = uploadError.message;
        }
        
        console.error('❌ [Canva API] Final error message:', errorMessage);
        
        // Don't do generic auth detection if we already have structured errors
        if (!isStructuredError) {
          // Check if it's a generic auth error  
          if (errorMessage.includes('401') || errorMessage.includes('unauthorized') || 
              errorMessage.includes('Authorization required') || errorMessage.includes('Invalid token') ||
              errorMessage.includes('Invalid JWT') || errorMessage.includes('Missing authorization header')) {
            throw new Error('Your session has expired. Please refresh the page and log in again to continue using Canva integration.');
          }
          
          // Check if it's a CORS/network error
          if (errorMessage.includes('CORS') || errorMessage.includes('ERR_FAILED')) {
            throw new Error('Network error: Unable to connect to Canva service. Please check your connection and try again.');
          }
        }
        
        throw new Error(errorMessage);
      }

      console.log('✅ [Canva API Direct] Asset uploaded successfully:', uploadResult.asset.id);
      console.error('🚨 [CRITICAL] WHAT CANVA ACTUALLY RECEIVED:', {
        assetId: uploadResult.asset.id,
        uploadedURL: uploadResult.asset.url || 'NO URL RETURNED',
        originalCropURL: imageUrl,
        optimizedCropURL: optimizedImageUrl,
        uploadSuccess: !!uploadResult.success
      });

      // Create design with custom dimensions for 1:1 aspect ratio
      const designPayload: any = {
        collectionId: asset.collection_id,
        title: `Edit: ${asset.title || 'Asset'}`,
        canvaAssetId: uploadResult.asset.id
      };
      
      // Add dimensions if specified (for 1:1 aspect ratio)
      if (options?.dimensions) {
        designPayload.dimensions = options.dimensions;
        console.log('🎨 [Canva API] Creating design with custom dimensions:', options.dimensions);
      }
      
      const { data: designResult, error: designError } = await invokeEdgeFunction(
        'canva-create-design',
        designPayload
      );

      if (designError || !designResult?.success) {
        // Check if it's an auth error
        if (designError?.message?.includes('401') || designError?.message?.includes('unauthorized')) {
          throw new Error('Canva authentication expired. Please reconnect your Canva account in Settings and try again.');
        }
        throw new Error(designError?.message || 'Failed to create Canva design');
      }

      // Use the imageType already determined at the top of the function
      const designKey = `canva_design_${asset.id}_${imageType}`;
      
      const newDesignInfo = {
        designId: designResult.design.id,
        editUrl: designResult.design.urls.edit_url,
        assetId: asset.id,
        imageType: imageType, // Store which image this design is for
        originalImageUrl: imageUrl,
        canvaAssetId: uploadResult.asset.id
      };

      // Store with image-specific key so primary and secondary don't conflict
      localStorage.setItem(designKey, JSON.stringify(newDesignInfo));
      console.log(`✅ [Canva Storage] Stored design for ${imageType} image with key:`, designKey);
      const canvaEditUrl = designResult.design.urls.edit_url;
      console.log('✅ [Canva API Direct] Design created successfully:', designResult.design.id);

    // Navigate to Canva immediately
    const correlationState = {
      originPage: options?.redirect?.originPage || 'ASSET_EDITOR',
      assetId: asset.id,
      collectionId: asset.collection_id,
      reopenCropEditor: options?.reopenCropEditor || false,
      customReturnPath: options?.redirect?.customReturnPath,
      ...options?.redirect?.additionalState
    };

    console.log('🔗 [Canva API Direct] Creating navigate URL with correlation state:', correlationState);
    console.log('🔗 [Canva API Direct] Base edit URL from Canva:', canvaEditUrl);
    
    // Store correlation state in localStorage as backup
    const actualDesignId = canvaEditUrl.includes('/design/') ? canvaEditUrl.split('/design/')[1].split('/')[0] : 'temp';
    const correlationKey = `canva_correlation_${actualDesignId}`;
    localStorage.setItem(correlationKey, JSON.stringify(correlationState));
    console.log('🔗 [Canva API Direct] Stored correlation state in localStorage with key:', correlationKey);
    
    // Create navigate URL with correlation state
    const redirectUrl = new URL(canvaEditUrl);
    const encodedCorrelationState = btoa(JSON.stringify(correlationState));
    redirectUrl.searchParams.append(
      "correlation_state", 
      encodeURIComponent(encodedCorrelationState)
    );
    const navigateUrl = redirectUrl.toString();
    console.log('🔗 [Canva API Direct] Final navigate URL with correlation state:', navigateUrl);

    if (toastFn) {
      toastFn({
        title: "Opening in Canva",
        description: "Redirecting to Canva editor...",
        duration: 1000,
      });
    }

    setTimeout(() => {
      console.log('🚀 [Canva API Direct] Redirecting to Canva:', navigateUrl);
      window.open(navigateUrl, "_self");
    }, 500);

  } catch (error) {
    console.error('❌ [Canva API Direct] Error in edit flow:', error);
    if (toastFn) {
      toastFn({
        title: "Edit Failed",
        description: error instanceof Error ? error.message : "Failed to start Canva editing.",
        variant: "destructive",
      });
    }
  }
};

/**
 * Optimize image URL for higher quality upload to Canva
 */
function optimizeImageUrlForCanva(imageUrl: string): string {
  if (!imageUrl) return imageUrl;

  // For Cloudinary URLs, optimize for quality
  if (imageUrl.includes('cloudinary.com')) {
    // Remove existing quality parameters and add high quality settings
    let optimizedUrl = imageUrl;
    
    // Remove existing quality/format params if they exist
    optimizedUrl = optimizedUrl.replace(/\/q_\w+/g, '');
    optimizedUrl = optimizedUrl.replace(/\/f_\w+/g, '');
    optimizedUrl = optimizedUrl.replace(/\/dpr_[\d.]+/g, '');
    
    // 🚨 CRITICAL FIX: Don't modify URLs that already have crop parameters OR markup URLs
    // The crop URL should be used AS-IS to maintain exact viewport match
    // Markup URLs should also be used AS-IS to maintain exact design integrity
    if (optimizedUrl.includes('c_crop') || optimizedUrl.includes('collections/markup')) {
      const reason = optimizedUrl.includes('c_crop') ? 'crop parameters' : 'markup URL';
      console.log(`🚨 [CRITICAL] URL has ${reason} - returning AS-IS to preserve integrity:`, optimizedUrl);
      return optimizedUrl; // Don't modify crop or markup URLs at all
    }
    
    // Add high quality parameters only for non-crop URLs
    const qualityParams = 'q_100,f_png,fl_preserve_transparency,dpr_2.0';
    
    if (optimizedUrl.includes('/upload/')) {
      // Insert quality params after /upload/
      optimizedUrl = optimizedUrl.replace('/upload/', `/upload/${qualityParams}/`);
    } else if (optimizedUrl.includes('/image/upload/')) {
      // Insert quality params after /image/upload/
      optimizedUrl = optimizedUrl.replace('/image/upload/', `/image/upload/${qualityParams}/`);
    }
    
    console.log('🎨 [Canva Optimize] Enhanced Cloudinary URL for upload:', {
      added_params: qualityParams,
      url_length_change: optimizedUrl.length - imageUrl.length,
      original_has_crop: imageUrl.includes('c_crop'),
      optimized_has_crop: optimizedUrl.includes('c_crop'),
      original_crop_params: imageUrl.match(/c_crop[^\/]*/)?.[0] || 'none',
      optimized_crop_params: optimizedUrl.match(/c_crop[^\/]*/)?.[0] || 'none',
      crop_parameters_preserved: imageUrl.includes('c_crop') ? optimizedUrl.includes(imageUrl.match(/c_crop[^\/]*/)?.[0] || '') : 'n/a'
    });
    
    return optimizedUrl;
  }
  
  // For non-Cloudinary URLs, try to use Cloudinary fetch with optimization
  if (imageUrl.startsWith('http://') || imageUrl.startsWith('https://')) {
    const optimizedUrl = `https://res.cloudinary.com/pricing-explorer/image/fetch/q_100,f_png,fl_preserve_transparency,dpr_2.0/${encodeURIComponent(imageUrl)}`;
    
    console.log('🎨 [Canva Optimize] Converting to high-quality Cloudinary fetch URL:', {
      original_domain: new URL(imageUrl).hostname,
      now_using_cloudinary: true
    });
    
    return optimizedUrl;
  }
  
  console.log('🎨 [Canva Optimize] URL not optimizable, using as-is');
  return imageUrl;
}

export { CanvaApiError, CanvaApi };